import PaymentSystemInterface from 'app/interfaces/PaymentSystemInterface';
import React, {useCallback, useEffect} from 'react';
import DepositSystem from '../form/DepositSystem';
import P2pDepositsStoreProps from 'app/store/P2pDepositsStoreProps';
import {DepositsStoreProps} from 'app/store/DepositsStore';
import {inject, observer} from 'mobx-react';
import CurrentP2pDeposit from './CurrentP2pDeposit';
import DepositAmount from 'app/components/deposit/form/DepositAmount';
import DepositSubmit from 'app/components/deposit/form/DepositSubmit';
import SelectPicker from 'app/components/reusable/SelectPicker';
import {calculateAmountBySelectedCurrency} from 'app/utils/deposits/calculateAmountBySelectedCurrency';
import RelatedBankSelect from './RelatedBankSelect';
import CountrySelect from './CountrySelect';
import SelectBonusType from 'app/components/user/SelectBonusType';
import {userStoreInterface} from 'app/store/UserStore';
import DepositPromoCode from 'app/components/deposit/form/DepositPromoCode';
import {leavePage} from './LeavePage';
import {useBlocker} from 'react-router';
import {ALL_BANKS} from 'app/store/P2pDepositsStore';
import {fixed2int} from 'app/utils/fixed';
import I18n from 'app/i18n';
import {roundAmount} from 'app/utils/deposits/roundAmount';
import {exchangedAmount} from 'app/utils/deposits/exchangedAmount';
import {CURRENCY_TO_ROUND} from 'app/utils/deposits/roundByCurrency';
import {getRateByCurrency} from 'app/utils/deposits/getRateByCurrency';
import {isoCurrencyToHtml} from 'app/components/deposit/depositExchangeAmount';

interface P2pDepositProps extends P2pDepositsStoreProps, DepositsStoreProps, userStoreInterface {}

const THREE_SECOND = 3000;

const P2pDeposit: React.FC<P2pDepositProps> = ({depositsStore, p2pDepositsStore, userStore}) => {
    useEffect(() => {
        p2pDepositsStore.loadP2PCountries(system().id);
        p2pDepositsStore.selectBankById(ALL_BANKS, true);

        if (p2pDepositsStore.p2p_countries.length > 0) {
            p2pDepositsStore.selectCountry(p2pDepositsStore.p2p_countries[0].country_code);
        } else {
            p2pDepositsStore.setCurrencies();
            p2pDepositsStore.loadP2pDeposits(system().id);
        }

        const updateInterval = setInterval(
            () => p2pDepositsStore.loadP2pDeposits(system().id),
            THREE_SECOND
        );

        return () => {
            clearInterval(updateInterval);
        };
    }, [p2pDepositsStore, depositsStore]);

    useBlocker(() => leavePage(p2pDepositsStore));


    useEffect(() => {
        const preventUnload = (event: BeforeUnloadEvent) => {
            const {current_p2p_deposit} = p2pDepositsStore;

            if (!current_p2p_deposit || !current_p2p_deposit.working) {
                return false;
            }

            event.preventDefault();
            event.returnValue = '';
            return true;
        };

        window.addEventListener('beforeunload', preventUnload);

        return () => {
            window.removeEventListener('beforeunload', preventUnload);
        };
    }, []);

    const system = (): PaymentSystemInterface => depositsStore.currentPaymentSystem;

    const onSubmit = (): void => {
        const {amount, rates, bonus_type, promoCode, selectedCountry} = depositsStore;
        const {currency, startDepositByAmount} = p2pDepositsStore;
        const newAmount = calculateAmountBySelectedCurrency(amount, currency, rates);
        const isValidate = validateAmount(Number(newAmount));
        startDepositByAmount(newAmount, {bonus: bonus_type, promoCode, selectedCountry, showToastError: isValidate});
    };

    const validateAmount = (amount: number): boolean => {
        const {rates, currentPaymentSystem} = depositsStore;
        const {currency} = p2pDepositsStore;
        const {min_limit, max_limit} = currentPaymentSystem;
        const rate = getRateByCurrency(currency, rates);
        const currency_sign = currency && currency !== 'USD' ? `${isoCurrencyToHtml(currency)}` : '$';
        const calculateCurrency = (limit: string) => CURRENCY_TO_ROUND.includes(currency) && rate
            ? fixed2int(roundAmount(Number(limit), currency, rates))
            : fixed2int(exchangedAmount(rate, limit));

        if (amount < Number(min_limit)) {
            depositsStore.resetErrors(
                {error: I18n.t('amount_min_error_with_currency', {amount: `${currency_sign}${calculateCurrency(min_limit)}`})}
            );
            return false;
        } else if (amount > Number(max_limit)) {
            depositsStore.resetErrors(
                {error: I18n.t('amount_max_error_with_currency', {amount: `${currency_sign}${calculateCurrency(max_limit)}`})}
            );
            return false;
        }

        return true;
    };

    const handleOnSubmit = useCallback(() => onSubmit(), [onSubmit]);

    const currencyChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        p2pDepositsStore.selectCurrency(e.target.value);
        depositsStore.resetErrors();
    };

    const handleCurrencyChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => currencyChange(e), [currencyChange]);

    const {firstDeposit} = depositsStore;
    const {user} = userStore;
    const {current_p2p_deposit, currency, currencies, currentCountry} = p2pDepositsStore;
    const systemData = system();
    const {amount_input} = systemData;

    return current_p2p_deposit
        ? <CurrentP2pDeposit current_p2p_deposit={current_p2p_deposit} system={systemData} firstDeposit={firstDeposit} />
        : <>
            <DepositSystem>
                <div className="form__row-selects">
                    {system().p2p_same_country && <CountrySelect />}
                    {currencies &&
                                <div className="form__row form__row-select">
                                    <SelectPicker
                                        onChange={handleCurrencyChange}
                                        value={currency}
                                        collection={currencies}
                                        className="select-bank" />
                                </div>
                    }
                    <RelatedBankSelect />
                </div>
                {amount_input && <>
                    <DepositAmount currency={currency} exchange_currency={currentCountry?.currency}/>
                    {(!user.first_deposit || user.first_bonus_not_applied) && !user.bonuses_disabled &&
                        <SelectBonusType />
                    }

                    <DepositPromoCode/>
                    <DepositSubmit onSubmit={handleOnSubmit}/>
                </>}
                {!user.first_deposit && !user.bonuses_disabled && !amount_input && <SelectBonusType />}
            </DepositSystem>
        </>;
};

export default inject('depositsStore', 'p2pDepositsStore', 'userStore')(observer(P2pDeposit));
